/*
Camera helper for the PWA capture flow.

IN-PAGE CAPTURE (getUserMedia), not the native camera intent.

Why: `<input type=file capture>` launches the OS camera *app* via an Intent,
which **backgrounds our tab**. On Android (Vanadium/GrapheneOS) the
low-memory killer then terminates the backgrounded renderer to free RAM for
the camera app — the tab reloads on return and the in-flight photo is lost
(reproduced on the 2nd capture, device 8 GB RAM). Device logs proved it:
`document.wasDiscarded=false` (a process kill, not a graceful discard) right
after `[life] visibility=hidden` (tab backgrounded), and reducing our own
memory footprint did NOT help — being backgrounded at all is the trigger.

The fix: capture *inside the page* with `navigator.mediaDevices.getUserMedia`
+ a `<video>` preview + a shutter that grabs the current frame to a canvas.
The tab never backgrounds, so the renderer is never killed. We downscale
everything to 1600 px Q85 downstream anyway, so a 1920×1080 stream frame is
more than enough. Falls back to the legacy `<input capture>` only when
getUserMedia is unavailable (very old WebViews).

The captured Blob lands in IndexedDB under the 'photos' store, keyed by a
generated UUID filename — the same naming scheme the Android app uses.
*/

const kAcceptedMime: string = 'image/*'  // legacy <input> fallback only
const kCaptureFacing: 'environment' | 'user' = 'environment'  // back camera for tree shots
const kStreamWidthIdeal: number = 1920  // requested capture resolution; downstream re-encode caps the long edge at 1600 px
const kStreamHeightIdeal: number = 1080
const kJpegQuality: number = 0.92  // canvas → JPEG quality before the storage re-encode (Q85) shrinks it further
const kFocusSingleShotMode: string = 'single-shot'  // Chromium image-capture focusMode used for tap-to-focus
const kRefocusContinuousDelayMs: number = 3_500  // hold the tap focus this long, then hand back to continuous AF
const kFocusRingDurationMs: number = 600  // must match the CSS @keyframes camera-focus-ring duration

export interface CapturedPhoto
{
	filename: string
	blob: Blob
	takenAt: number
}

/*
Non-standard MediaStreamTrack image-capture extensions (zoom / focusMode) are
absent from lib.dom.d.ts. We declare just the slices we touch so the calls stay
typed without an `any` escape hatch — getCapabilities()/applyConstraints() are
read defensively (optional-chained, try/catch) since support is device-specific.
*/
interface ZoomCapability { min: number; max: number; step?: number }
interface ExtendedCapabilities { zoom?: ZoomCapability; focusMode?: string[] }
interface ExtendedConstraintSet { zoom?: number; focusMode?: string; pointsOfInterest?: Array<{ x: number; y: number }> }
type ExtendedTrack = Omit<MediaStreamTrack, 'getCapabilities' | 'applyConstraints'> & {
	getCapabilities?: () => ExtendedCapabilities
	applyConstraints?: (c: { advanced?: ExtendedConstraintSet[] }) => Promise<void>
}

/*
Open the camera and resolve with the captured photo, or reject if the user
cancels. Uses in-page getUserMedia when available (keeps the tab foreground),
falling back to the legacy native-intent picker otherwise.

`inOnCommitted` fires SYNCHRONOUSLY the instant the user validates their shot
(camera overlay already torn down, JPEG encode still pending) — the caller
uses it to put up the capture splash without a visible gap. The legacy
<input> fallback can't observe the validation moment, so it never fires it.
*/
export function capturePhoto(inOnCommitted?: () => void): Promise<CapturedPhoto>
{
	const vMedia = navigator.mediaDevices
	if (vMedia && typeof vMedia.getUserMedia === 'function') return capturePhotoViaStream(inOnCommitted)
	return capturePhotoViaInput()
}

/*
In-page capture: full-screen overlay with a live <video> preview and a
shutter. The tab stays foreground throughout, so the renderer is never
backgrounded → never killed by the OS during the capture (see the file
header). Cancel + permission/hardware errors reject with a friendly message
the capture flow surfaces.
*/
function capturePhotoViaStream(inOnCommitted?: () => void): Promise<CapturedPhoto>
{
	return new Promise<CapturedPhoto>((inResolve, inReject) => {
		const vUi = buildCameraOverlay()
		document.body.appendChild(vUi.root)

		let fStream: MediaStream | null = null  // active media stream, stopped on settle
		let fSettled: boolean = false  // single-fire guard across shutter / cancel / error
		let fDetachPinch: (() => void) | null = null  // teardown for the pinch-to-zoom listeners
		let fFocusCtl: TapFocusController | null = null  // tap-to-focus controller, no-op on devices without single-shot AF
		let fMode: 'live' | 'review' = 'live'  // 'live' = preview running; 'review' = frame frozen, awaiting validate/retake
		let fReviewCanvas: HTMLCanvasElement | null = null  // captured (cropped) frame, held until the user validates

		const stopStream = (): void => {
			if (!fStream) return
			for (const vTrack of fStream.getTracks()) { try { vTrack.stop() } catch { /* already stopped */ } }
			fStream = null
		}
		const settle = (inFn: () => void): void => {
			if (fSettled) return
			fSettled = true
			if (fDetachPinch) { fDetachPinch(); fDetachPinch = null }
			if (fFocusCtl) { fFocusCtl.detach(); fFocusCtl = null }
			stopStream()
			vUi.root.remove()
			inFn()
		}

		// Capture the current frame to fReviewCanvas and freeze the preview on it.
		// drawImage is synchronous, so the photo is grabbed the instant the user
		// taps — pausing the <video> then "displays" that exact frame (the stream
		// + track stay live so Reprendre can resume). No toBlob yet: that runs only
		// on Valider, so the user sees instant feedback instead of a 3-4 s hang.
		const enterReview = (): void => {
			const vVideo = vUi.video
			if (!vVideo.videoWidth || !vVideo.videoHeight) return  // stream not ready yet — ignore early taps
			// WYSIWYG crop: the <video> is shown object-fit:cover, so the on-screen
			// frame is the centre region of the sensor frame that fills the element's
			// box. Capture exactly that region (not the whole landscape sensor frame)
			// so the saved photo matches the preview — and comes out portrait when the
			// phone is held portrait. The track-level zoom is already baked into the
			// video frame, so this naturally captures the zoomed image too.
			const vCrop = coverCropRegion(
				vVideo.videoWidth, vVideo.videoHeight,
				vVideo.clientWidth || vVideo.videoWidth, vVideo.clientHeight || vVideo.videoHeight,
			)
			const vCanvas = document.createElement('canvas')
			vCanvas.width = vCrop.sw
			vCanvas.height = vCrop.sh
			const vCtx = vCanvas.getContext('2d')
			if (!vCtx) { settle(() => inReject(new Error('Canvas 2D indisponible.'))); return }
			vCtx.drawImage(vVideo, vCrop.sx, vCrop.sy, vCrop.sw, vCrop.sh, 0, 0, vCrop.sw, vCrop.sh)
			fReviewCanvas = vCanvas
			vVideo.pause()  // freeze the on-screen preview on the captured frame
			fFocusCtl?.cancel()  // drop any pending tap focus; AF is continuous again if the user retakes
			fMode = 'review'
			vUi.setMode('review')
		}

		const backToLive = (): void => {
			fReviewCanvas = null
			fMode = 'live'
			vUi.setMode('live')
			void vUi.video.play().catch(() => { /* resumes; ignore */ })
		}

		const validate = (): void => {
			const vCanvas = fReviewCanvas
			if (!vCanvas) return
			if (fSettled) return
			// Tear the overlay down IMMEDIATELY: the validation tap must hand
			// over to the next surface (capture splash) in the same frame —
			// leaving the frozen review on screen while toBlob encodes (1-2 s
			// on device) reads as a hang with still-clickable buttons. The
			// frame lives in fReviewCanvas; encoding doesn't need the DOM.
			fSettled = true
			if (fDetachPinch) { fDetachPinch(); fDetachPinch = null }
			if (fFocusCtl) { fFocusCtl.detach(); fFocusCtl = null }
			stopStream()
			vUi.root.remove()
			inOnCommitted?.()
			try
			{
				vCanvas.toBlob((inBlob) => {
					if (!inBlob) { inReject(new Error('Échec de la capture image.')); return }
					inResolve({ filename: `${crypto.randomUUID()}.jpg`, blob: inBlob, takenAt: Date.now() })
				}, 'image/jpeg', kJpegQuality)
			}
			catch (vErr)
			{
				inReject(vErr instanceof Error ? vErr : new Error('Capture échouée.'))
			}
		}

		// Left button: cancel the whole capture in live mode, or step back to the
		// live preview from review mode.
		vUi.cancelBtn.addEventListener('click', () => {
			if (fMode === 'review') backToLive()
			else settle(() => inReject(new Error('Capture cancelled')))
		})

		// Main button: take the shot (→ review) in live mode, or commit it in review.
		vUi.shutterBtn.addEventListener('click', () => {
			if (fMode === 'review') validate()
			else enterReview()
		})

		// Kick off the stream. getUserMedia must be reached within the user
		// activation from the Capturer tap — capturePhoto() is the first call
		// in runCaptureFlow, before any await, so activation is still fresh.
		navigator.mediaDevices.getUserMedia({
			video: {
				facingMode: { ideal: kCaptureFacing },
				width: { ideal: kStreamWidthIdeal },
				height: { ideal: kStreamHeightIdeal },
			},
			audio: false,
		}).then((inStream) => {
			if (fSettled) { for (const vTrack of inStream.getTracks()) { try { vTrack.stop() } catch { /* */ } }; return }  // cancelled during the await
			fStream = inStream
			vUi.video.srcObject = inStream
			void vUi.video.play().catch(() => { /* autoplay+muted+playsinline usually plays; ignore */ })
			vUi.setReady()
			// Best-effort track tuning: continuous autofocus + pinch-to-zoom +
			// tap-to-focus. All are device-specific capabilities; absent ones are
			// simply skipped and never break the capture.
			const vTrack = inStream.getVideoTracks()[0] as ExtendedTrack | undefined
			if (vTrack)
			{
				enableContinuousAutofocus(vTrack)
				fDetachPinch = attachPinchZoom(vUi.root, vTrack, () => fMode === 'live')
				fFocusCtl = attachTapToFocus(vUi.root, vUi.video, vTrack, () => fMode === 'live')
			}
		}).catch((inErr: unknown) => {
			settle(() => inReject(mapGetUserMediaError(inErr)))
		})
	})
}

/*
Compute the sensor-frame sub-region that an object-fit:cover <video> actually
shows on screen. Returns the source rectangle (sx, sy, sw, sh) to pass to
drawImage so the captured canvas matches the visible preview exactly. When the
display box is more portrait than the sensor frame we keep full height and trim
width (centred vertical band), and vice-versa.
*/
function coverCropRegion(
	inFrameW: number, inFrameH: number,
	inViewW: number, inViewH: number,
): { sx: number; sy: number; sw: number; sh: number }
{
	const vFrameRatio = inFrameW / inFrameH
	const vViewRatio = inViewW / inViewH
	if (vViewRatio < vFrameRatio)
	{
		// display is narrower than the frame → crop width (vertical band)
		const vSw = Math.round(inFrameH * vViewRatio)
		return { sx: Math.round((inFrameW - vSw) / 2), sy: 0, sw: vSw, sh: inFrameH }
	}
	// display is wider than the frame → crop height (horizontal band)
	const vSh = Math.round(inFrameW / vViewRatio)
	return { sx: 0, sy: Math.round((inFrameH - vSh) / 2), sw: inFrameW, sh: vSh }
}

/*
Ask the camera for continuous autofocus, best-effort. The focusMode constraint
is non-standard and unsupported on many devices; failures are swallowed so the
capture is never blocked.
*/
function enableContinuousAutofocus(inTrack: ExtendedTrack): void
{
	try
	{
		const vCaps = inTrack.getCapabilities?.()
		if (vCaps?.focusMode?.includes('continuous') && inTrack.applyConstraints)
			void inTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => { /* device rejected — ignore */ })
	}
	catch { /* getCapabilities unavailable — ignore */ }
}

interface TapFocusController
{
	cancel: () => void  // entering review: drop the pending refocus timer + ring, restore continuous AF
	detach: () => void  // closing the camera: remove the listener; no AF re-apply, the track is being stopped
}

/*
Tap-to-focus on the live preview: tap a point → focus there via the Chromium
image-capture constraints (`focusMode: 'single-shot'` + `pointsOfInterest`),
show a brief aiming ring at the tap, then hand back to continuous AF after a
few seconds — the standard camera-app behaviour. The Android app gets this
for free from the system camera intent; the in-page getUserMedia preview has
to provide it itself.

Best-effort like the rest of the track tuning: when the device doesn't
advertise the single-shot focus mode, no listener is attached and the
controller is a no-op — no ring either, so the UI never pretends a focus
happened. The tap coordinates are mapped through the same object-fit:cover
crop as the captured frame (coverCropRegion), so the focused point is the
one under the finger, not its position on the full sensor frame.
*/
function attachTapToFocus(
	inRoot: HTMLElement,
	inVideo: HTMLVideoElement,
	inTrack: ExtendedTrack,
	inIsLive: () => boolean,
): TapFocusController
{
	let vFocusModes: string[] | undefined
	try { vFocusModes = inTrack.getCapabilities?.().focusMode }
	catch { vFocusModes = undefined }
	if (!vFocusModes?.includes(kFocusSingleShotMode) || !inTrack.applyConstraints)
		return { cancel: () => { /* no tap-to-focus on this device */ }, detach: () => { /* idem */ } }

	let fRing: HTMLElement | null = null  // the aiming ring currently on screen, one at a time
	let fRingTimer: number | null = null
	let fRefocusTimer: number | null = null  // pending hand-back to continuous AF

	const removeRing = (): void => {
		if (fRingTimer !== null) { clearTimeout(fRingTimer); fRingTimer = null }
		fRing?.remove()
		fRing = null
	}
	const clearRefocus = (): void => {
		if (fRefocusTimer !== null) { clearTimeout(fRefocusTimer); fRefocusTimer = null }
	}

	const onTap = (inEv: MouseEvent): void => {
		if (!inIsLive()) return  // frozen review frame — nothing to focus
		if (!inVideo.videoWidth || !inVideo.videoHeight) return  // stream not ready yet
		// Tap → normalised frame coordinates (0..1), through the cover crop:
		// the on-screen image is the centre band of the sensor frame.
		const vRect = inVideo.getBoundingClientRect()
		if (vRect.width <= 0 || vRect.height <= 0) return
		const vCrop = coverCropRegion(inVideo.videoWidth, inVideo.videoHeight, vRect.width, vRect.height)
		const vX = clamp01((vCrop.sx + ((inEv.clientX - vRect.left) / vRect.width) * vCrop.sw) / inVideo.videoWidth)
		const vY = clamp01((vCrop.sy + ((inEv.clientY - vRect.top) / vRect.height) * vCrop.sh) / inVideo.videoHeight)
		void inTrack.applyConstraints?.({
			advanced: [{ focusMode: kFocusSingleShotMode, pointsOfInterest: [{ x: vX, y: vY }] }],
		}).catch(() => { /* device rejected — ignore */ })
		// Aiming ring at the tap point (root is fixed inset:0 → client coords
		// are root coords). Self-removes when its CSS animation ends.
		removeRing()
		const vRing = document.createElement('div')
		vRing.className = 'camera-focus-ring'
		vRing.style.left = `${inEv.clientX}px`
		vRing.style.top = `${inEv.clientY}px`
		inRoot.appendChild(vRing)
		fRing = vRing
		fRingTimer = window.setTimeout(removeRing, kFocusRingDurationMs)
		// Hold the tap focus, then return to continuous AF.
		clearRefocus()
		fRefocusTimer = window.setTimeout(() => {
			fRefocusTimer = null
			enableContinuousAutofocus(inTrack)
		}, kRefocusContinuousDelayMs)
	}

	inVideo.addEventListener('click', onTap)

	return {
		cancel: (): void => {
			clearRefocus()
			removeRing()
			enableContinuousAutofocus(inTrack)  // AF is clean again for a « Reprendre »
		},
		detach: (): void => {
			clearRefocus()
			removeRing()
			inVideo.removeEventListener('click', onTap)
		},
	}
}

function clamp01(inV: number): number
{
	return Math.min(1, Math.max(0, inV))
}

/*
Wire two-finger pinch to the track's optical/digital zoom (where supported).
Returns a teardown that removes the listeners. If the device exposes no zoom
capability, no listeners are attached and the teardown is a no-op. applyConstraints
is async and would saturate under the touchmove flood, so we coalesce to the last
requested value via requestAnimationFrame.
*/
function attachPinchZoom(inRoot: HTMLElement, inTrack: ExtendedTrack, inIsActive: () => boolean): () => void
{
	let vZoomCap: ZoomCapability | undefined
	try { vZoomCap = inTrack.getCapabilities?.().zoom }
	catch { vZoomCap = undefined }
	if (!vZoomCap || !inTrack.applyConstraints) return () => { /* no zoom on this device */ }

	const vMin = vZoomCap.min
	const vMax = vZoomCap.max
	const vStep = vZoomCap.step && vZoomCap.step > 0 ? vZoomCap.step : 0
	let fZoom: number = clampZoom(readCurrentZoom(inTrack) ?? vMin, vMin, vMax)
	let fStartDist: number = 0  // finger distance at pinch start
	let fStartZoom: number = fZoom  // zoom level at pinch start
	let fPending: number | null = null  // last requested zoom awaiting the rAF flush
	let fRaf: number | null = null

	const flush = (): void => {
		fRaf = null
		if (fPending === null) return
		const vTarget = fPending
		fPending = null
		void inTrack.applyConstraints?.({ advanced: [{ zoom: vTarget }] }).catch(() => { /* transient — next move retries */ })
	}
	const request = (inZoom: number): void => {
		fZoom = inZoom
		fPending = inZoom
		if (fRaf === null) fRaf = requestAnimationFrame(flush)
	}

	const onStart = (inEv: TouchEvent): void => {
		if (!inIsActive() || inEv.touches.length !== 2) return  // ignore pinch while the frame is frozen in review mode
		fStartDist = touchDistance(inEv)
		fStartZoom = fZoom
	}
	const onMove = (inEv: TouchEvent): void => {
		if (!inIsActive() || inEv.touches.length !== 2 || fStartDist <= 0) return
		inEv.preventDefault()  // block the browser's native page pinch-zoom
		const vRatio = touchDistance(inEv) / fStartDist
		let vNext = clampZoom(fStartZoom * vRatio, vMin, vMax)
		if (vStep > 0) vNext = Math.round(vNext / vStep) * vStep
		request(clampZoom(vNext, vMin, vMax))
	}
	const onEnd = (inEv: TouchEvent): void => { if (inEv.touches.length < 2) fStartDist = 0 }

	inRoot.addEventListener('touchstart', onStart, { passive: false })
	inRoot.addEventListener('touchmove', onMove, { passive: false })
	inRoot.addEventListener('touchend', onEnd)
	inRoot.addEventListener('touchcancel', onEnd)

	return (): void => {
		if (fRaf !== null) { cancelAnimationFrame(fRaf); fRaf = null }
		inRoot.removeEventListener('touchstart', onStart)
		inRoot.removeEventListener('touchmove', onMove)
		inRoot.removeEventListener('touchend', onEnd)
		inRoot.removeEventListener('touchcancel', onEnd)
	}
}

function clampZoom(inV: number, inMin: number, inMax: number): number
{
	return Math.min(inMax, Math.max(inMin, inV))
}

function readCurrentZoom(inTrack: ExtendedTrack): number | undefined
{
	try { return (inTrack.getSettings() as { zoom?: number }).zoom }
	catch { return undefined }
}

function touchDistance(inEv: TouchEvent): number
{
	const vDx = inEv.touches[0].clientX - inEv.touches[1].clientX
	const vDy = inEv.touches[0].clientY - inEv.touches[1].clientY
	return Math.hypot(vDx, vDy)
}

interface CameraOverlay
{
	root: HTMLElement
	video: HTMLVideoElement
	shutterBtn: HTMLButtonElement
	cancelBtn: HTMLButtonElement
	setReady: () => void  // hide the "starting…" status + enable the shutter once the stream plays
	setMode: (inMode: 'live' | 'review') => void  // swap shutter→✓ + cancel→Reprendre, and back
}

function buildCameraOverlay(): CameraOverlay
{
	const vRoot = document.createElement('div')
	vRoot.className = 'camera-capture'
	vRoot.setAttribute('role', 'dialog')
	vRoot.setAttribute('aria-label', 'Caméra')

	const vVideo = document.createElement('video')
	vVideo.className = 'camera-video'
	vVideo.autoplay = true
	vVideo.muted = true
	vVideo.playsInline = true  // iOS: keep the stream inline, don't go full-screen native player
	vVideo.setAttribute('playsinline', '')  // belt-and-suspenders for older WebKit attribute parsing

	const vStatus = document.createElement('div')
	vStatus.className = 'camera-status'
	vStatus.textContent = 'Démarrage de la caméra…'

	const vBar = document.createElement('div')
	vBar.className = 'camera-bar'

	const vCancel = document.createElement('button')
	vCancel.type = 'button'
	vCancel.className = 'camera-cancel'
	vCancel.textContent = 'Annuler'

	const vShutter = document.createElement('button')
	vShutter.type = 'button'
	vShutter.className = 'camera-shutter'
	vShutter.setAttribute('aria-label', 'Prendre la photo')
	vShutter.disabled = true  // until the stream is playing
	// Green checkmark, hidden in live mode and revealed (via .is-confirm) in
	// review mode so the shutter reads as a "Valider" button.
	vShutter.innerHTML = '<svg class="camera-shutter-check" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'

	const vSpacer = document.createElement('div')
	vSpacer.className = 'camera-spacer'  // balances the cancel button so the shutter stays centred

	vBar.appendChild(vCancel)
	vBar.appendChild(vShutter)
	vBar.appendChild(vSpacer)

	vRoot.appendChild(vVideo)
	vRoot.appendChild(vStatus)
	vRoot.appendChild(vBar)

	return {
		root: vRoot,
		video: vVideo,
		shutterBtn: vShutter,
		cancelBtn: vCancel,
		setReady: (): void => { vStatus.remove(); vShutter.disabled = false },
		setMode: (inMode): void => {
			const vReview = inMode === 'review'
			vShutter.classList.toggle('is-confirm', vReview)
			vShutter.setAttribute('aria-label', vReview ? 'Valider la photo' : 'Prendre la photo')
			vCancel.textContent = vReview ? 'Reprendre' : 'Annuler'
		},
	}
}

/*
Map a getUserMedia rejection to a user-facing French message. The capture
flow shows non-cancel rejections verbatim, so keep these short + actionable.
*/
function mapGetUserMediaError(inErr: unknown): Error
{
	const vName = (inErr as { name?: string } | null)?.name ?? ''
	switch (vName)
	{
		case 'NotAllowedError':
		case 'SecurityError':
			return new Error('Accès caméra refusé. Autorise la caméra pour CanoPaname.')
		case 'NotFoundError':
		case 'OverconstrainedError':
			return new Error('Aucune caméra arrière trouvée sur cet appareil.')
		case 'NotReadableError':
			return new Error('Caméra déjà utilisée par une autre application.')
		default:
			return new Error(inErr instanceof Error && inErr.message ? inErr.message : 'Caméra indisponible.')
	}
}

/*
Legacy fallback — native camera intent via a hidden <input type=file capture>.
Only used when getUserMedia is unavailable. This path DOES background the tab
(the OS may evict the renderer while the camera intent is foreground), so it's
a last resort for ancient WebViews.

Cancel detection: the native `cancel` event is unreliable on old WebViews, so
a focus-watch fallback treats "page regained foreground with no change event
within kCancelGraceMs" as an annulation.
*/
const kCancelGraceMs: number = 15_000  // Vanadium / Android picker can take 5–10 s to deliver the file — shorter windows fired false-positive cancels

function capturePhotoViaInput(): Promise<CapturedPhoto>
{
	return new Promise<CapturedPhoto>((inResolve, inReject) => {
		const vInput = document.createElement('input')  // ephemeral file picker
		vInput.type = 'file'
		vInput.accept = kAcceptedMime
		vInput.setAttribute('capture', kCaptureFacing)
		vInput.style.display = 'none'

		let fSettled: boolean = false  // single-fire guard across change / cancel / focus-fallback
		let fCancelTimer: number | null = null  // setTimeout id armed by the focus fallback
		const settle = (inFn: () => void): void => {
			if (fSettled) return
			fSettled = true
			if (fCancelTimer !== null) { window.clearTimeout(fCancelTimer); fCancelTimer = null }
			window.removeEventListener('focus', onWindowFocus)
			document.removeEventListener('visibilitychange', onVisibilityChange)
			if (vInput.parentNode) vInput.parentNode.removeChild(vInput)
			inFn()
		}

		const armCancelFallback = (): void => {
			if (fSettled || fCancelTimer !== null) return
			fCancelTimer = window.setTimeout(() => {
				fCancelTimer = null
				settle(() => inReject(new Error('Capture cancelled')))
			}, kCancelGraceMs)
		}

		const onWindowFocus = (): void => { armCancelFallback() }
		const onVisibilityChange = (): void => { if (!document.hidden) armCancelFallback() }

		vInput.addEventListener('change', () => {
			const vFile = vInput.files?.[0]  // first (and only) selected file
			if (!vFile)
			{
				settle(() => inReject(new Error('No photo selected')))
				return
			}
			settle(() => inResolve({
				filename: `${crypto.randomUUID()}.jpg`,
				blob: vFile,
				takenAt: Date.now(),
			}))
		}, { once: true })

		vInput.addEventListener('cancel', () => {
			settle(() => inReject(new Error('Capture cancelled')))
		}, { once: true })

		window.addEventListener('focus', onWindowFocus)
		document.addEventListener('visibilitychange', onVisibilityChange)

		document.body.appendChild(vInput)
		vInput.click()
	})
}
